import { ReactNode, useState } from "react";
import { Box } from "@mui/material";
import { useNetworkAwareImage } from "@/hooks/useNetworkAwareImage";

export interface BrandedProject {
  name: string;
  logo_url?: string | null;
  banner_url?: string | null;
  background_url?: string | null;
}

interface BrandedContainerProps {
  project: BrandedProject;
  /** Background color for the page (behind the image if present) */
  backgroundColor?: string;
  /** Background color for the content card (hex format recommended) */
  cardBackgroundColor?: string;
  /** Text color for the project name */
  textColor?: string;
  /** Max width for the content card (default: 800) */
  maxWidth?: number;
  children: ReactNode;
}

export default function BrandedContainer({
  project,
  backgroundColor = "#f5f5f5",
  cardBackgroundColor = "#ffffff",
  textColor,
  maxWidth = 800,
  children,
}: BrandedContainerProps) {
  const [imageErrors, setImageErrors] = useState({
    banner: false,
    logo: false,
  });

  // Preload background image with network-aware timeout
  const { loaded: backgroundLoaded, error: backgroundError } =
    useNetworkAwareImage({
      url: project.background_url,
      enabled: !!project.background_url,
      logAnalytics: (analyticsData) => {
        console.warn("Background image loading issue:", analyticsData);
      },
    });

  const hasBackground =
    project.background_url && backgroundLoaded && !backgroundError;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        position: "relative",
        backgroundColor,
        backgroundImage: hasBackground
          ? `url(${project.background_url})`
          : "none",
        ...(hasBackground && {
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundAttachment: "fixed",
          "@media (hover: none) and (pointer: coarse)": {
            backgroundAttachment: "scroll",
          },
        }),
      }}
    >
      {/* Background overlay for readability */}
      {hasBackground && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.4)",
            backdropFilter: "blur(2px)",
            zIndex: 0,
          }}
        />
      )}

      {/* Centered content */}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          py: 4,
          px: 2,
        }}
      >
        <Box
          sx={{
            p: { xs: 3, sm: 4, md: 5 },
            maxWidth,
            width: "100%",
            backgroundColor: hasBackground
              ? `${cardBackgroundColor}FA`
              : cardBackgroundColor,
            backdropFilter: hasBackground ? "blur(20px)" : "none",
            borderRadius: 2,
            boxShadow: hasBackground
              ? "0 8px 32px rgba(0, 0, 0, 0.12)"
              : undefined,
          }}
        >
          {/* Banner */}
          {project.banner_url && !imageErrors.banner && (
            <Box
              sx={{
                mb: 4,
                mx: { xs: -3, sm: -4, md: -5 },
                mt: { xs: -3, sm: -4, md: -5 },
              }}
            >
              <img
                src={project.banner_url}
                alt="Project banner"
                loading="lazy"
                onError={() => {
                  setImageErrors((prev) => ({ ...prev, banner: true }));
                }}
                style={{
                  width: "100%",
                  height: "auto",
                  borderRadius: "8px 8px 0 0",
                  display: "block",
                }}
              />
            </Box>
          )}

          {/* Logo + Project Name */}
          {(project.logo_url || project.name) && (
            <Box
              sx={{
                mb: 4,
                display: "flex",
                alignItems: "center",
                gap: 3,
                flexDirection: "column",
                textAlign: "center",
                justifyContent: "center",
              }}
            >
              {project.logo_url && !imageErrors.logo && (
                <Box
                  component="img"
                  src={project.logo_url}
                  alt="Project logo"
                  loading="lazy"
                  onError={() => {
                    setImageErrors((prev) => ({ ...prev, logo: true }));
                  }}
                  sx={{
                    width: { xs: 80, sm: 100 },
                    height: { xs: 80, sm: 100 },
                    objectFit: "contain",
                    borderRadius: 2,
                    boxShadow: 1,
                  }}
                />
              )}
              <Box
                component="h1"
                sx={{
                  fontWeight: 600,
                  color: textColor || "text.primary",
                  fontSize: { xs: "1.75rem", sm: "2.125rem", md: "2.5rem" },
                  m: 0,
                  fontFamily: (theme) => theme.typography.h4.fontFamily,
                  lineHeight: (theme) => theme.typography.h4.lineHeight,
                  letterSpacing: (theme) => theme.typography.h4.letterSpacing,
                }}
              >
                {project.name}
              </Box>
            </Box>
          )}

          {/* Content slot */}
          {children}
        </Box>
      </Box>
    </Box>
  );
}
